// shamelessly stolen form https://dev.to/n8tb1t/tracking-scroll-position-with-react-hooks-3bbj
import { useRef, useLayoutEffect, useCallback, useEffect } from 'react';
import { useEnvironment } from '@wix/yoshi-flow-editor';

export type Pos = { x: number; y: number; isAtPageBottom: boolean };

function getScrollPosition(isSSR: boolean): Pos {
  if (isSSR) {
    return { x: 0, y: 0, isAtPageBottom: false };
  }

  const { left, top } = document.body.getBoundingClientRect();
  const isAtPageBottom =
    window.innerHeight + window.scrollY === document.body.scrollHeight;
  return { x: left, y: top, isAtPageBottom };
}

export type useScrollPositionOptions = {
  waitFor?: number;
  disabled?: boolean;
};

export function useScrollPosition(
  effect: ({ prevPos, currPos }: { prevPos: Pos; currPos: Pos }) => void,
  deps: any[],
  options: useScrollPositionOptions = {},
) {
  options = {
    waitFor: 100,
    disabled: false,
    ...options,
  };

  const { isSSR, isMobile } = useEnvironment();

  if (!isMobile) {
    options.disabled = true;
  }

  const position = useRef(getScrollPosition(isSSR));
  let throttleTimeout: number | null = null;
  const callBack = () => {
    const currPos = getScrollPosition(isSSR);
    effect({ prevPos: position.current, currPos });
    position.current = currPos;
    throttleTimeout = null;
  };
  const useIsomorphicLayoutEffect = isSSR ? useEffect : useLayoutEffect;
  useIsomorphicLayoutEffect(() => {
    if (isSSR) {
      return;
    }
    const handleScroll = () => {
      if (throttleTimeout === null) {
        throttleTimeout = window.setTimeout(callBack, options.waitFor);
      }
    };

    if (!options.disabled) {
      window.addEventListener('scroll', handleScroll);
      const cleanup = () => {
        window.removeEventListener('scroll', handleScroll);
        if (throttleTimeout) {
          window.clearTimeout(throttleTimeout);
        }
      };
      return cleanup;
    }
    return () => {};
  }, deps);
}
